import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { matchesShortcut } from '../../config/shortcuts';
import { isEditableTarget } from '../../utils/keyboard';
import { usePreferencesStore } from '../../store/preferencesStore';
import { cloudIsFullNow, isDeviceScope, deviceScopeId } from '../../records/itemLocation';
import { isWebApp } from '../../platform/target';
import {
  countOwnDocuments,
  createFolder,
  createDocument,
  renameNode,
  deleteNodes,
} from '../../extensions/bundled/documents/documentsStore';
import { requireLocalCapacityFor, requireCloudSync } from '../../plans/localGate';
import { locationActionFor } from '../../plans/cloudAccess';
import { copyDocumentToDevice, moveDocumentToCloud, moveDocumentToDevice } from '../../records/itemMove';
// Tells MCP subscribers the tree moved (notifications/resources/list_changed).
// Called directly after each user write rather than via bumpDocumentsTree():
// the sidebar reloads itself with load(), so a nonce bump would only add a
// second tree read. MCP's own writes use the same store functions and must
// NOT notify (S6), which is why this lives at the UI call site, not in the store.
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { openDocumentTab, closeDocumentTab, updateDocumentTabLabel, updateDocumentTabScope } from '../../store/tabsStore';
import { addNewDocToActiveProject } from '../../scope/docSpaceActions';
import { docWireScope } from './useDocProjectRows';

/**
 * A section's write actions: create, rename, delete, cloud <-> device move,
 * the titlebar "new" events and the rename shortcut. `tree` is the result of
 * useSectionTree for the same section.
 */
export default function useSectionActions({
  sectionId,
  scope,
  listenTitlebarEvents,
  tree,
  selected,
  setSelected,
  editing,
  setEditing,
}) {
  const { load, endKey, expandFolder, ensureSectionExpanded, collectSubtree } = tree;
  // The user's remap map, so the rename binding below follows Settings.
  const keyboardShortcuts = usePreferencesStore((s2) => s2.keyboardShortcuts);
  const [cloudFull, setCloudFull] = useState(false);
  // Pending cascade delete awaiting confirmation: { subtree, title, message }.
  const [deleteRequest, setDeleteRequest] = useState(null);

  const handleNewFolder = useCallback(
    async (parentId) => {
      if (!parentId) ensureSectionExpanded();
      const node = await createFolder({ parentId, name: 'New Folder', sortKey: endKey(parentId) }, scope);
      notifyMcpSurfaceChanged('documents');
      // Owner decision 0.2: a top-level folder made while a project is active
      // joins it (a nested one is in through its parent).
      if (!parentId) await addNewDocToActiveProject(`doc_folder:${node.id}`, docWireScope(scope));
      if (parentId) expandFolder(parentId);
      await load();
      setSelected({ sectionId, id: node.id });
      setEditing({ sectionId, id: node.id });
    },
    [endKey, ensureSectionExpanded, expandFolder, load, scope, sectionId, setEditing, setSelected]
  );

  const handleNewDocument = useCallback(
    async (parentId) => {
      // Web only: nothing is kept locally here, so a document past the cloud
      // limit would be refused by the server and lost on the next reload.
      //
      // A SUBPAGE counts too — documentsStore writes `record_type: 'document'`
      // whatever its parent, and the server counts those rows — so the earlier
      // root-only check let exactly the failure it exists to prevent through.
      // A TEAM document is not governed by the personal quota at all
      // (Plans.PersonalSyncQuota: "team sync is NOT governed here"), so a Free
      // member of a paying team must not be stopped in their team's section.
      const personalScope = !scope?.type || scope.type === 'personal';
      if (personalScope && isWebApp() && (await cloudIsFullNow('documents'))) {
        setCloudFull(true);
        return;
      }
      // Plans v2: Free makes 5 documents of its own (team documents don't count).
      if (scope?.type !== 'team' && !(await requireLocalCapacityFor('documents', countOwnDocuments))) return;
      if (!parentId) ensureSectionExpanded();
      const { id, scopeKey } = await createDocument({ parentId, title: '', sortKey: endKey(parentId) }, scope);
      notifyMcpSurfaceChanged('documents');
      if (!parentId) await addNewDocToActiveProject(`document:${id}`, docWireScope(scope));
      if (parentId) expandFolder(parentId);
      await load();
      // The store may have put a new root document on this device instead of
      // the cloud (card C5), so open the tab in the scope it actually landed
      // in rather than the one this section asked for.
      if (isDeviceScope(scopeKey) !== (scope?.type === 'device')) bumpDocumentsTree();
      openDocumentTab({ documentId: id, label: i18n.t('docSidebar.untitled'), scope: isDeviceScope(scopeKey) ? { type: 'device' } : scope });
    },
    [endKey, ensureSectionExpanded, expandFolder, load, scope]
  );

  // The app titlebar mirrors these two root-level actions (see
  // ExtensionTitleBarActions) via window events, but only for Personal — a
  // team section's own "+" buttons on its header cover the same actions there.
  const newDocRef = useRef(null);
  const newFolderRef = useRef(null);
  newDocRef.current = () => handleNewDocument(null);
  newFolderRef.current = () => handleNewFolder(null);
  useEffect(() => {
    if (!listenTitlebarEvents) return undefined;
    const onDoc = () => newDocRef.current?.();
    const onFolder = () => newFolderRef.current?.();
    window.addEventListener('documents-new-document', onDoc);
    window.addEventListener('documents-new-folder', onFolder);
    return () => {
      window.removeEventListener('documents-new-document', onDoc);
      window.removeEventListener('documents-new-folder', onFolder);
    };
  }, [listenTitlebarEvents]);

  const handleRename = async (node, name) => {
    setEditing(null);
    if (name && name !== node.name) {
      await renameNode(node, name, scope);
      notifyMcpSurfaceChanged('documents');
      if (node.type === 'document') updateDocumentTabLabel(node.id, name);
      await load();
    }
  };

  // Cloud <-> this device (card C6). Only a signed-in desktop user has a
  // device scope at all, and only a TOP-LEVEL personal document can move: a
  // subpage follows its parent, and team documents are always in the cloud.
  const onDeviceSection = scope?.type === 'device';
  const [movingDocId, setMovingDocId] = useState(null);
  const canMoveDocumentLocation = useCallback(
    (node) =>
      Boolean(deviceScopeId()) &&
      node?.type === 'document' &&
      !node?.parentId &&
      (onDeviceSection || !scope?.type || scope.type === 'personal'),
    [onDeviceSection, scope]
  );
  const moveDocumentLocation = useCallback(
    async (node) => {
      if (!node?.id || movingDocId) return;
      // Plans v2 card 3.4: by capability — see plans/cloudAccess.locationActionFor.
      const action = locationActionFor(onDeviceSection ? 'device' : 'cloud');
      if (action === 'upgrade') {
        requireCloudSync();
        return;
      }
      setMovingDocId(node.id);
      try {
        const result =
          action === 'toCloud'
            ? await moveDocumentToCloud(node.id)
            : action === 'download'
              ? await copyDocumentToDevice(node.id)
              : await moveDocumentToDevice(node.id);
        if (!result?.refused) {
          // An open tab still saves into the scope the document just left,
          // which would re-create the copy the user asked us to remove. The
          // editor only re-locates on mount, so point it at the new scope here.
          updateDocumentTabScope(node.id, onDeviceSection ? { type: 'personal' } : { type: 'device' });
          // The write crossed scopes, so THIS section's load() is not enough:
          // the other section has to reload too, or the document vanishes from
          // the tree until the next full refresh.
          bumpDocumentsTree();
          notifyMcpSurfaceChanged('documents');
        }
      } catch (err) {
        console.warn('[documents] moving the document failed:', err);
      } finally {
        setMovingDocId(null);
        await load();
      }
    },
    [load, movingDocId, onDeviceSection]
  );

  const isSelected = selected?.sectionId === sectionId;
  const selectedId = isSelected ? selected.id : null;
  const isEditingSection = editing?.sectionId === sectionId;
  const editingId = isEditingSection ? editing.id : null;

  // Renames the selected node on the `rename` command (Ctrl/Cmd+E, or F2).
  // Only fires while this section's node is the one currently selected.
  //
  // Asks the shortcut registry rather than testing keys by hand. The hand-rolled
  // check it replaces was blind to a remap: rebind rename in Settings and
  // Collections followed while Documents kept answering to the old keys, with
  // nothing to indicate the two had diverged. `matchesShortcut` resolves the
  // user's override and still honours the fixed F2 alt.
  //
  // The global handler (useKeyboardShortcuts.js) listens in the CAPTURE phase
  // and this one bubbles, so it sees the event first — it yields whenever the
  // Collections sidebar is not the active panel, which is what lets this run.
  useEffect(() => {
    if (!selectedId || editingId) return;
    const onKeyDown = (e) => {
      if (!matchesShortcut(e, 'rename', keyboardShortcuts)) return;
      // Typing beats renaming: while the cursor is in a field the combo belongs
      // to that field (Ctrl+E is end-of-line on macOS).
      if (isEditableTarget(e.target)) return;
      const t = e.target;
      if (t && t.classList && t.classList.contains('tab-dropdown-search-input')) return;
      e.preventDefault();
      setEditing({ sectionId, id: selectedId });
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedId, editingId, sectionId, setEditing, keyboardShortcuts]);

  // Deleting any node deletes its whole subtree — documents can hold subpages
  // now, so a document delete must cascade exactly like a folder delete or it
  // orphans the children (rows still in the store, unreachable from the root).
  //
  // The prompt is a ConfirmModal, NOT window.confirm: `confirm` is a Tauri
  // dialog-plugin command and this app's capabilities grant only `dialog:default`
  // — which is allow-message/save/open, with no allow-confirm. So window.confirm
  // did not return false in the packaged app, it THREW ("dialog.confirm not
  // allowed. Command not found") out of handleDelete, and the delete simply
  // never happened. Same reason DbManagerResultsTab confirms in-app.
  const performDelete = async (subtree) => {
    // Tabs close FIRST so their unmount flushes fire before (or harmlessly
    // during) the delete — but ordering alone cannot win this race:
    // closeDocumentTab only schedules the unmount, and the flush runs async
    // from its cleanup effect. The real fix is deleteNodes marking the ids in
    // deletedDocsGuard synchronously, which turns any late flush into a no-op.
    // This ordering just spares the guard from swallowing avoidable writes.
    for (const n of subtree) if (n.type === 'document') closeDocumentTab(n.id);
    await deleteNodes(subtree, scope);
    notifyMcpSurfaceChanged('documents');
    await load();
  };

  const handleDelete = async (node) => {
    const subtree = collectSubtree(node.id);
    // A childless node keeps the immediate, no-confirm delete.
    if (subtree.length <= 1) return performDelete(subtree);
    const docCount = subtree.filter((n) => n.type === 'document').length;
    setDeleteRequest({
      subtree,
      title: i18n.t(node.type === 'folder' ? 'docSidebar.delete.folderTitle' : 'docSidebar.delete.documentTitle'),
      message:
        node.type === 'folder'
          ? i18n.t(docCount ? 'docSidebar.delete.folderMessageDocs' : 'docSidebar.delete.folderMessage', {
              name: node.name || i18n.t('docSidebar.delete.thisFolder'),
              count: subtree.length - 1,
              docs: i18n.t('docSidebar.delete.docCount', { count: docCount }),
            })
          : i18n.t('docSidebar.delete.pageMessage', {
              name: node.name || i18n.t('docSidebar.delete.thisDocument'),
              count: subtree.length - 1,
            }),
    });
  };

  return {
    handleNewFolder,
    handleNewDocument,
    handleRename,
    onDeviceSection,
    movingDocId,
    canMoveDocumentLocation,
    moveDocumentLocation,
    selectedId,
    editingId,
    cloudFull,
    setCloudFull,
    deleteRequest,
    setDeleteRequest,
    performDelete,
    handleDelete,
  };
}
